import { algoliasearch, SearchClient } from "algoliasearch";
import { Hit } from "instantsearch.js";
import { useState, useEffect, useCallback, useMemo, useContext } from "react";
import {
  InstantSearch,
  Index,
  useInfiniteHits,
  useSearchBox,
  Configure,
  useStats,
} from "react-instantsearch";
import actions from "src/actions";
import {
  AlgoliaIndexType,
  AlgoliaSegmentObject,
} from "common/interfaces/Algolia";
import {
  Box,
  Button,
  HStack,
  Icon,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import KYDSearchInput from "src/ui/KYDSearchInput";
import { AddIcon } from "@chakra-ui/icons";
import CreateNewSegmentDialog from "./CreateNewSegmentDialog";
import { SegmentsTable } from "./SegmentsTable";
import DeleteSegmentDialog, {
  DeleteSegmentDialogContext,
  withDeleteSegmentDialog,
} from "./DeleteSegmentDialog";
import { KYDSegment } from "@common/interfaces/KYDCampaign";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";

const Segments = ({ permissions = [] }: { permissions?: KYDScope[] }) => {
  const [segmentsAlgoliaConfig, setSegmentsAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  }>(null);

  const [contactsAlgoliaConfig, setContactsAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  }>(null);

  const fetchContactsConfig = useCallback(async () => {
    const fetchConfig = await actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.CONTACTS,
    });
    if (fetchConfig.events_config) {
      const search = algoliasearch(
        fetchConfig.events_config.app_id,
        fetchConfig.events_config.api_key
      );
      search.clearCache();
      setContactsAlgoliaConfig({
        indexName: fetchConfig.events_config.index_name,
        searchClient: search,
      });
    }
  }, []);

  const fetchSegmentsAlgoliaConfig = useCallback(async () => {
    const fetchConfig = await actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.SEGMENTS,
    });
    if (fetchConfig.events_config) {
      const search = algoliasearch(
        fetchConfig.events_config.app_id,
        fetchConfig.events_config.api_key
      );
      search.clearCache();
      setSegmentsAlgoliaConfig({
        indexName: fetchConfig.events_config.index_name,
        searchClient: search,
      });
    }
  }, []);

  useEffect(() => {
    fetchContactsConfig();
    fetchSegmentsAlgoliaConfig();
  }, [fetchContactsConfig, fetchSegmentsAlgoliaConfig]);

  if (!segmentsAlgoliaConfig?.indexName || !contactsAlgoliaConfig?.indexName) {
    return <Spinner />;
  }

  return (
    <InstantSearch
      indexName={segmentsAlgoliaConfig.indexName}
      searchClient={segmentsAlgoliaConfig.searchClient}
    >
      <SegmentsTableWrapper permissions={permissions} />
    </InstantSearch>
  );
};

const createLocalHit = (
  segment: AlgoliaSegmentObject,
  position: number = 0
): Hit<AlgoliaSegmentObject> => ({
  ...segment,
  __position: position,
});

const SegmentsTableWrapper = ({
  permissions = [],
}: {
  permissions?: KYDScope[];
}) => {
  const [isCreateSegmentDialogOpen, setIsCreateSegmentDialogOpen] =
    useState(false);

  const {
    open: openDeleteSegmentDialog,
    isOpen: isDeleteSegmentDialogOpen,
    close: closeDeleteSegmentDialog,
  } = useContext(DeleteSegmentDialogContext);
  const toast = useToast();

  const [updatingSegmentId, setUpdatingSegmentId] = useState<string | null>(
    null
  );
  const [editingSegmentId, setEditingSegmentId] = useState<string | null>(null);

  // Local state for optimistic updates
  const [localSegments, setLocalSegments] = useState<
    Hit<AlgoliaSegmentObject>[]
  >([]);
  const [deletedSegmentIds, setDeletedSegmentIds] = useState<string[]>([]);

  const { items: _segments } = useInfiniteHits<AlgoliaSegmentObject>({
    escapeHTML: false,
    showPrevious: false,
  });

  // Merge Algolia and local segments, prioritizing local changes and filtering out deleted segments
  const segments = useMemo(() => {
    // Remove deleted segments from Algolia hits
    const filteredAlgolia = _segments.filter(
      (seg) => !deletedSegmentIds.includes(seg.objectID)
    );
    // Remove Algolia segments that are overridden by localSegments
    const algoliaWithoutLocals = filteredAlgolia.filter(
      (seg) => !localSegments.some((local) => local.objectID === seg.objectID)
    );
    // Add localSegments (created/updated) first, then the rest from Algolia
    return [...localSegments, ...algoliaWithoutLocals];
  }, [_segments, localSegments, deletedSegmentIds]);

  // Create or update a segment optimistically
  const onCreate = (segment: AlgoliaSegmentObject) => {
    const newSegment = createLocalHit({
      ...segment,
      objectID: segment.objectID || `local-${Date.now()}`,
      created_at_unix: Math.floor(Date.now() / 1000),
    });
    setLocalSegments((prev) => {
      // Replace if exists, else add
      const filtered = prev.filter((s) => s.objectID !== newSegment.objectID);
      return [newSegment, ...filtered];
    });
    // Remove from deleted if it was previously deleted
    setDeletedSegmentIds((prev) =>
      prev.filter((id) => id !== newSegment.objectID)
    );
  };

  // Update a segment optimistically
  const onUpdate = (segment: AlgoliaSegmentObject) => {
    const updatedSegment = createLocalHit(segment);
    setLocalSegments((prev) => {
      const filtered = prev.filter(
        (s) => s.objectID !== updatedSegment.objectID
      );
      return [updatedSegment, ...filtered];
    });
    setDeletedSegmentIds((prev) =>
      prev.filter((id) => id !== updatedSegment.objectID)
    );
  };

  const handleClickDelete = (segment: KYDSegment) => {
    console.log("handleClickDelete", segment);
    openDeleteSegmentDialog({
      text: `Are you sure you want to delete ${segment.name || "this segment"}?`,
      title: "Delete Segment",
      actions: [
        {
          text: "Delete",
          type: "danger",
          onClick: () => handleDelete(segment),
          buttonProps: {
            bg: "red.500",
            color: "white",
            w: "auto",
          },
        },
        {
          text: "Cancel",
          type: "secondary",
          onClick: closeDeleteSegmentDialog,
        },
      ],
    });
  };

  const handleDelete = async (segment) => {
    const id = segment.objectID;
    setUpdatingSegmentId(id);
    setDeletedSegmentIds((prev) => [...prev, id]);
    setLocalSegments((prev) => prev.filter((s) => s.objectID !== id));
    closeDeleteSegmentDialog();
    try {
      await actions.ADMIN.deleteSegment(id);
      toast({
        title: "Segment deleted",
        description: "Segment deleted successfully",
        status: "success",
      });
    } catch (error) {
      console.error("Failed to delete segment:", error);
      toast({
        title: "Failed to delete segment",
        description: "Please try again",
        status: "error",
      });
      setDeletedSegmentIds((prev) => prev.filter((segId) => segId !== id));
    } finally {
      setUpdatingSegmentId(null);
    }
  };

  const handleDuplicate = async (id: string) => {
    setUpdatingSegmentId(id);
    try {
      const response = await actions.ADMIN.duplicateSegment(id);
      onCreate(response);
      toast({
        title: "Segment duplicate started",
        description: "Segment is being duplicated",
        status: "success",
      });
    } catch (error) {
      console.error("Failed to delete segment:", error);
      toast({
        title: "Failed to delete segment",
        description: "Please try again",
        status: "error",
      });
    } finally {
      setUpdatingSegmentId(null);
    }
  };

  const { refine } = useSearchBox();

  return (
    <VStack w="100%" alignItems="flex-start">
      <ShowIfHasScope
        permissions={permissions}
        checkFor={["crm/segments:write", "crm/write"]}
      >
        <>
          <CreateNewSegmentDialog
            onCreate={onCreate}
            isOpen={isCreateSegmentDialogOpen}
            close={() => setIsCreateSegmentDialogOpen(false)}
          />
          <DeleteSegmentDialog onClose={() => {}} />
        </>
      </ShowIfHasScope>
      <HStack
        pb={6}
        alignItems="center"
        justifyContent="space-between"
        w="100%"
      >
        <HStack alignItems="center" gap={2}>
          <Text
            color="#1A202C"
            fontFamily="Work Sans"
            fontSize="32px"
            fontStyle="normal"
            fontWeight={400}
            lineHeight="30px"
            letterSpacing="-0.96px"
          >
            All Segments
          </Text>
          <Text
            color="rgba(26, 32, 44, 0.50)"
            fontFamily="Work Sans"
            fontSize="32px"
            fontStyle="normal"
            fontWeight={400}
            lineHeight="30px"
            letterSpacing="-0.96px"
          >
            {segments.length}
          </Text>
        </HStack>
        <ShowIfHasScope
          permissions={permissions}
          checkFor={["crm/segments:write", "crm/write"]}
        >
          <Button
            rightIcon={<Icon as={AddIcon} />}
            borderRadius="full"
            onClick={() => {
              setIsCreateSegmentDialogOpen(true);
            }}
            bg="black"
            gap={1}
            textColor="white"
            _hover={{ bg: "gray.800" }}
            fontSize="16px"
          >
            <Text pl={2}>New Segment</Text>
          </Button>
        </ShowIfHasScope>
      </HStack>

      <HStack w="100%" justifyContent="space-between">
        <KYDSearchInput
          w={260}
          leftIcon={<Icon as={FiSearch} color={"#969696"} />}
          items={[]}
          placeholder="Search for segment by name"
          onChange={refine}
        />
      </HStack>

      <Box w="100%" overflowX="auto" mt={6}>
        <SegmentsTable
          permissions={permissions}
          rows={segments}
          handleEdit={setEditingSegmentId}
          handleDuplicate={handleDuplicate}
          handleDelete={handleClickDelete}
          updatingSegmentId={updatingSegmentId}
          editingSegmentId={editingSegmentId}
        />
      </Box>
    </VStack>
  );
};

const ContactsStatsWithLoading = () => {
  const stats = useStats();

  if (!stats.processingTimeMS) {
    return <Spinner size="sm" />;
  }
  return (
    <Text
      textAlign="left"
      color="#1A202C"
      fontSize="24px"
      fontWeight={600}
      lineHeight="120%"
      letterSpacing="-0.72px"
    >
      {stats.nbHits} Contacts
    </Text>
  );
};

const SegmentsWithDialog = withDeleteSegmentDialog(Segments);
export default SegmentsWithDialog;
